import React from 'react'

import { Box } from '@mui/material'
import { useTheme, type Theme } from '@mui/material/styles'
import { LINK_UNDERLINE_OFFSET, LINK_UNDERLINE_THICKNESS, brandText } from '@repo/ui'

import { useGlossaryTerm } from 'docs/GlossaryProvider'

import { InfoTooltip } from '../InfoTooltip'

/**
 * How a term of art looks inside a sentence, in ONE place.
 *
 * Exported because `TermProse` needs the identical treatment for the references it
 * renders inside a glossary definition, and it cannot reuse `Term` itself: `Term` opens
 * `TermOverlay`, and one of `TermProse`'s two call sites IS that overlay (§52 — a
 * surface must not stack on itself). Two components, one appearance, one definition of
 * it — otherwise the same word is underlined two different ways depending on which
 * surface the reader met it on, and nothing would ever compare them (§36).
 *
 * `cursor` is the one genuine difference. On `Term` nothing navigates, so it is `help`.
 * Where activating a reference actually changes what is on screen it is `pointer`.
 */
export const termInlineSx = (theme: Theme, cursor: 'help' | 'pointer' = 'help') => ({
  cursor,
  color: 'inherit',
  textDecoration: 'underline',
  textDecorationStyle: 'dotted' as const,
  textDecorationThickness: LINK_UNDERLINE_THICKNESS,
  textUnderlineOffset: LINK_UNDERLINE_OFFSET,
  textDecorationColor: brandText(theme),
  '&:hover': { color: brandText(theme), textDecorationStyle: 'solid' },
  outline: 'none',
  '&:focus-visible': {
    color: brandText(theme),
    textDecorationStyle: 'solid',
    outline: `2px solid ${brandText(theme)}`,
    outlineOffset: '2px',
    borderRadius: '2px',
  },
})

interface Props {
  /** A glossary entry id. The definition is read from the glossary, never passed in. */
  id: string
  /** The words as they appear in the sentence. Never the id, never the entry's `term`. */
  children: React.ReactNode
}

/**
 * A term of art INSIDE a sentence, carrying its own definition.
 *
 * This is the fourth of the four glossary consumers working-conventions §44 names — the
 * glossary page, guide prose, tooltips, search — and it was the one that had never been
 * built. With no way to reference a definition inline, every guide that needed to say what
 * a word meant retyped it, which is precisely the drift a single source exists to prevent
 * and the drift nothing compares: no check anywhere reads a guide's sentence against the
 * glossary's.
 *
 * Authors write it as `[the words they want](term:<id>)` and `DocContent` routes the scheme
 * here — see `publish-guides.mjs` `TERM_REF_RE` for why the syntax is a link, and for the
 * sanitizer trap that makes the renderer's `urlTransform` load-bearing.
 *
 * ── Why it is quieter than a link ────────────────────────────────────────────────────
 *
 * A reference is not a destination. A guide's real links are `brandText` and go somewhere;
 * if a term looked the same, a paragraph naming five terms would read as five invitations to
 * leave, and the reader who followed one would lose the sentence they were in the middle of.
 * So COLOUR stays reserved for navigation and this takes the prose's own colour, marked only
 * by a DOTTED 1px rule at the same 3px offset the glossary page's links use — the long-
 * standing "there is a definition here" convention, related to the links it sits among
 * without competing with them. Hover and focus promote it: the text takes `brandText` and the
 * rule goes solid, which is the moment the affordance has to be unambiguous and the only
 * moment it costs the page nothing.
 *
 * `cursor: help` rather than `pointer` for the same reason — nothing here navigates.
 *
 * ── Why it composes `InfoTooltip` rather than opening its own thing ──────────────────
 *
 * Everything a reader wants from an inline term is already built: `short` verbatim on hover,
 * a footer that opens the full entry IN PLACE via `TermOverlay` when there is more to show,
 * and a real anchor to the glossary when there is not. Rebuilding any of that here would be
 * a third mechanism answering a question two already answer (§28, §47), and the copy would
 * drift the way three payment-method previews did (§36).
 *
 * One known cost, inherited and documented on `InfoTooltip`'s `children` prop: a custom
 * trigger is not cloned to carry the overlay's focus-restore ref, so dismissing the overlay
 * leaves focus on the document rather than back on this word. The tooltip itself, the
 * keyboard path into it, and the overlay all work; only the return trip is degraded. Fixing
 * it means teaching `InfoTooltip` to forward a ref into an arbitrary child, which is a change
 * to a component with dozens of call sites and does not belong in this one.
 *
 * ── Unresolved ids ───────────────────────────────────────────────────────────────────
 *
 * `useGlossaryTerm` resolves against the READER's corpus, so this renders as plain prose for
 * a term above their floor rather than as an empty affordance. That is the audience gate
 * working, not a failure — and it is not the fallback anything relies on, because
 * `publish-guides.mjs` fails the build on a reference a guide's own reader cannot open.
 */
export const Term = ({ id, children }: Props) => {
  const theme = useTheme()
  const entry = useGlossaryTerm(id)

  // Plain prose, no affordance. The build gate above means this is reachable only for a
  // reference that was legitimate for a different reader — so say the sentence and stop.
  if (!entry) return <>{children}</>

  return (
    <InfoTooltip term={id}>
      <Box
        component="span"
        tabIndex={0}
        // The default `InfoTooltip` trigger is a glyph and needs the question spelled out.
        // This one IS the word, so the label names what the affordance offers instead of
        // repeating text the screen reader has just read.
        aria-label={`${entry.term} — show definition`}
        // ONE `:focus-visible` block inside, not a second one merged with `:hover` —
        // duplicate keys in an `sx` object literal silently drop the first, and the two
        // states genuinely differ anyway: focus needs a ring that hover does not. The ring
        // is OFFSET rather than default, because the word already carries an underline and
        // a default outline would draw a second rule tight against it.
        sx={termInlineSx(theme)}
      >
        {children}
      </Box>
    </InfoTooltip>
  )
}
